import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, of, tap, throwError } from 'rxjs';
import { ApiConstants } from '../../shared/constants/api-constants';
import { Document } from '../models/document';
import { NextPhase } from '../models/nextPhase';
import { DocumentPreviousPhase } from '../models/documentPreviousPhase';
import { DocumentVersionReview } from '../models/documentVersionReview';
import { DocumentPhaseChange } from '../models/documentPhaseChange';
import { DocumentEffectiveAndPeriodicReviewDateDto } from '../models/documentEffectiveAndPeriodicReviewDateDTO';
import { DocumentSearch } from '../models/documentSearch';
import { DocumentAttachment } from '../models/document-attachment';
import { DocumentVersionLink } from '../models/documentVersionLink';
import { DocumentSourceFile } from '../models/documentSourceFile';
import { DocumentSourceFileEdit } from '../models/documentSourceFileEdit';
import { DocumentSourceFileDetails } from '../models/documentSourceFileDetails';
import { AssignmentGroupsRequest } from '../models/assignment-groups-request';
import { DocumentReadAudit } from '../models/document-read-audit';
import { DocumentAttachmentDetails } from '../models/document-attachment-details';
import { DocumentProficiencyQuiz } from '../models/document-proficiency-quiz';
import { MessageService } from 'primeng/api';
import { DocumentLibraryConstants } from '../document-library-constants';
import { NameCode } from '../../shared/models/name-code';
import { DocumentCommentDTO } from '../models/document-comment/documentCommentDTO';
import { UserReplacementDetails } from '../models/user-replacement-details';
import { TrainingItemGroupWithScheduled } from '../../shared/models/TrainingItemGroupWithScheduled';
import { PaginationState } from '../../shared-components/panel-hub/models/pagination-state';
import { AppliedFilters } from '../../shared-components/panel-hub/models/applied-filters';
import { SortCriteria } from '../../shared-components/panel-hub/models/sort-criteria';
import { SearchMapperService } from '../../shared/services/search-mapper.service';
import { DocumentQueryResult } from '../models/document-query-result';
import { PollCompletionService } from '../../core/services/poll-completion.service';
import { SmartLinksResponse } from '../models/smart-link-model';

@Injectable({
  providedIn: 'root',
})
export class DocumentService { 
  documentProficiencyQuizDetail: DocumentProficiencyQuiz = {} as DocumentProficiencyQuiz;
  documentDetails = {};
  documentLookUpData = {};
  documentBulkUserReplacementLookup = {};

  editDocumentSlider$ = new BehaviorSubject<boolean>(false);
  editDocumentMetaDataLoaded$ = new BehaviorSubject<boolean>(false);

  constructor(
    private httpClient: HttpClient,
    private messageService: MessageService,
      private searchMapper: SearchMapperService,
      private pollCompletionService: PollCompletionService
  ) { }

  getDocumentById(documentId: string): Observable<any> {
    const headers = new HttpHeaders().set('X-No-Retry', 'true');
    return this.httpClient.get(ApiConstants.GetDocumentById_Api + `${documentId}`, { headers: headers });
  }
  getDocumentdetails(): Observable<any> {
    let observable: Observable<any>;
    if (Object.keys(this.documentLookUpData).length === 0) {
      observable = this.httpClient
        .get(ApiConstants.Document_LookUpDetails_Api)
        .pipe(tap((documentData) => (this.documentLookUpData = documentData)));
    } else {
      observable = of(this.documentLookUpData);
    }
    return observable;
  }

  getFormattedFileURI(documentNumber: string): Observable<any> {
    return this.httpClient.get(ApiConstants.GetSmartLinksFile_Download_Api + `${documentNumber}`);
  }

  saveDocument(document: Document): Observable<any> {
    return this.httpClient.post(
      ApiConstants.Document_SaveDetails_api,
      document
    );
  }

  getDocumentDetail(
    documentNumber: string,
    isEffective: boolean
  ): Observable<any> {
    let observable: Observable<any>;
    if (Object.keys(this.documentDetails).length === 0) {
      observable = this.httpClient
        .get(ApiConstants.DocumentDetail_Api, {
          params: {
            number: documentNumber,
            isEffective: isEffective,
          },
        })
        .pipe(tap((documentData) => (this.documentDetails = documentData)));
    } else {
      observable = of(this.documentDetails);
    }
    return observable;
  }
  getDocumentDetailByVersion(
    documentNumber: string,
    version: number
  ): Observable<any> {
    return this.httpClient.get(ApiConstants.DocumentDetailByVersion_Api, {
      params: {
        number: documentNumber,
        version: version,
      },
    });
  }
  getLatestDocumentDetail(documentNumber: string): Observable<any> {
    return this.httpClient.get(ApiConstants.DocumentLatestVersion_Api, {
      params: {
        number: documentNumber,
      },
    });
  }
  updateDocument(document: Document): Observable<any> {
    return this.httpClient.put(ApiConstants.Document_SaveDetails_api, document);
  }

  updateClientSignatureAttachment(documentNumber: string, documentVersion: number, clientSignatureAttachment: any): Observable<any> {
    return this.httpClient.put(
      `${ApiConstants.Document_UpdateClientSignatureAttachment_Api}/${documentNumber}/${documentVersion}`,
      clientSignatureAttachment
    );
  }

  updateDocumentCheckoutInfo(documentNumber: any, documentVersion: any, checkOutInformation: any): Observable<any> {
    return this.httpClient.put(ApiConstants.Document_UpdateCheckOutInformation_api + '/' + documentNumber + '/' + documentVersion, checkOutInformation);
  }

  discardDocumentCheckoutInfo(documentNumber: any, documentVersion: any): Observable<any> {
    return this.httpClient.put(ApiConstants.Document_DiscardCheckOutInformation_api + '/' + documentNumber + '/' + documentVersion, '');
  }
  
  getSmartLinks(documentNumber: string): Observable<SmartLinksResponse> {
    return this.httpClient.get<SmartLinksResponse>(
        `${ApiConstants.GetSmartLinks_Api}${documentNumber}`
    );
}

  moveDocumentToNextPhase(
    documentNumber: string,
    documentVersion: number,
    documentNextPhase: NextPhase
  ) {
    return this.httpClient.post(
      ApiConstants.ChangeToNextPhase_Api +
      `${documentNumber}` +
      '/' +
      `${documentVersion}` +
      '/nextPhase',
      documentNextPhase
    );
  }

   getFormattedFileStatus(documentNumber: string, documentVersion: number) : Observable<any> {
    const headers = new HttpHeaders().set('X-No-Retry', 'true');
    return this.httpClient.get(
      ApiConstants.CheckFormattedFileStatus_Api +
      `${documentNumber}` +
      '/' +
      `${documentVersion}` +
      '/status', { headers: headers }
    );
  }



  getPhases(phase: number, isIncludeQAReview: boolean, isIncludesClientSignature:boolean): Observable<any> {
    return this.httpClient.get(ApiConstants.GetPhases_Api, {
      params: {
        phase: phase,
        includeQAReview: isIncludeQAReview,
        includesClientSignature: isIncludesClientSignature
      },
    });
  }

  moveDocumentToPreviousPhase(
    documentNumber: string,
    version: number,
    documentPreviousPhase: DocumentPreviousPhase
  ): Observable<any> {
    return this.httpClient.put(
      ApiConstants.MoveToPreviousPhase_Api +
      `${documentNumber}` +
      '/' +
      `${version}` +
      '/previousPhase',
      documentPreviousPhase
    );
  }

  getReviewStatus(documentNumber: string, version: string): Observable<any> {
    return this.httpClient.get(
      ApiConstants.Document_Version_Review_Api +
      '/' +
      `${documentNumber}` +
      '/' +
      `${version}`
    );
  }

  updateReviewStatus(
    documentVersionReview: DocumentVersionReview
  ): Observable<any> {
    return this.httpClient.put(
      ApiConstants.Document_Version_Review_Api + '/updateReviewStatus',
      documentVersionReview
    );
  }

  getApprovalStatus(documentNumber: string, version: string): Observable<any> {
    return this.httpClient.get(
      ApiConstants.Document_Version_Approval_Api +
      '/' +
      `${documentNumber}` +
      '/' +
      `${version}`
    );
  }


  moveDocumentToObsoletePhases(
    documentNumber: string,
    version: string,
    documentPhaseChange: DocumentPhaseChange
  ): Observable<any> {
    return this.httpClient.put(
      ApiConstants.Document_Version_Obsolete_Phase_Change_Api +
      `${documentNumber}` +
      '/' +
      `${version}` +
      '/deactivate',
      documentPhaseChange
    );
  }

  moveDocumentToCancelPhase(
    documentNumber: string,
    version: string,
    documentPhaseChange: DocumentPhaseChange
  ): Observable<any> {
    return this.httpClient.put(
      ApiConstants.Document_Version_Cancelled_Phase_Change_Api +
      `${documentNumber}` +
      '/' +
      `${version}` +
      '/cancel',
      documentPhaseChange
    );
  }

  moveDocumentToUncancel(
    documentNumber: string,
    version: string
  ): Observable<any> {
    return this.httpClient.put(
      ApiConstants.Document_Version_Uncancelled_Phase_Change_Api +
      `${documentNumber}` +
      '/' +
      `${version}` +
      '/uncancel',
      null
    );
  }

  approveDocument(documentEffectiveAndPeriodicReviewDto: DocumentEffectiveAndPeriodicReviewDateDto): Observable<any> {
    return this.httpClient.put(
      ApiConstants.Document_Version_Approval_Api,
      documentEffectiveAndPeriodicReviewDto
    );
  }
  getDocumentVersions(documentNumber: string): Observable<any> {
    return this.httpClient.get(ApiConstants.Document_Versions_Api, {
      params: {
        number: documentNumber,
      },
    });
  }
  getDocumentSearch(documentSearchCriteria: DocumentSearch): Observable<any> {
    return this.httpClient.post(
      ApiConstants.Document_Search_Api, documentSearchCriteria);
  }
  uploadFile(file: File, foldername: string): Observable<any> {
    const formData = new FormData();
    formData.append(file.name, file);
    return this.httpClient.post(
      ApiConstants.Files_Upload_Api + foldername, formData);
  }

  // To DO : Need to integrate with file service
  deleteFile(fileId: string): Observable<boolean> {
    return of(true);
  }
  deleteDocumentAttachments(deletedDocumentAttachmentIds: string[]): Observable<any> {
    return this.httpClient.post(
      ApiConstants.Document_Attachments_Api + '/deleteDocumentAttachments',
      deletedDocumentAttachmentIds
    );
  }
  saveDocumentAttachments(documentAttachments: DocumentAttachment[]): Observable<any> {
    return this.httpClient.post(
      ApiConstants.Document_Attachments_Api,
      documentAttachments
    );
  }

  getDocumentAttachments(documentNumber: string, documentVersion: number)
    : Observable<DocumentAttachmentDetails[]> {
    return this.httpClient.get<DocumentAttachmentDetails[]>(ApiConstants.Document_Attachments_Api
      + '/' + `${documentNumber}` + '/' + `${documentVersion}`);
  }

  downloadFile(fileId: string)
    : Observable<any> {
    return this.httpClient.get(ApiConstants.Files_Download_Api + `${fileId}`);
  }

  getTitleOfDocumentVersion(documentVersionLink: DocumentVersionLink, documentNumber: string, version: string): Observable<any> {
    return this.httpClient.post(ApiConstants.DocumentVersion_Title + `${documentNumber}` + '/' +
      `${version}` + '/documentVersionTitle', documentVersionLink);
  }

  getDocumentTemplatesForDropDown(eqmsScopeIds: string[]): Observable<any> {
    return this.httpClient.post(ApiConstants.GetDocumentTemplatesForDropdown_Api, eqmsScopeIds);
  }

  getDocumentTemplate(documentTemplateId: string): Observable<any> {
    return this.httpClient.get(ApiConstants.GetDocumentTemplate_Api + `${documentTemplateId}`);
  }

  addDocumentSourceFile(documentSourceFileDetails: DocumentSourceFileDetails, documentNumber: string, documentVersion: number): Observable<any> {
    return this, this.httpClient.post(ApiConstants.CreateDocumentSourceFile_API + `/${documentNumber}/${documentVersion}`, documentSourceFileDetails);
  }

  createDocumentSourceFile(documentSourceFile: DocumentSourceFile): Observable<any> {
    return this.httpClient.post(ApiConstants.CreateOrUpdateDocumentSourceFile_APi, documentSourceFile)
  }

  updateDocumentSourceFile(documentSourceFileDetails: DocumentSourceFileDetails, documentNumber: string, documentVersion: number): Observable<any> {
    return this, this.httpClient.put(ApiConstants.UpdateDocumentSourceFile_API + `/${documentNumber}/${documentVersion}`, documentSourceFileDetails);
  }

  editDocumentSourceFile(documentSourceFileId: string, documentSourceFileEdit: DocumentSourceFileEdit): Observable<any> {
    return this.httpClient.put(ApiConstants.CreateOrUpdateDocumentSourceFile_APi + "/" + `${documentSourceFileId}`, documentSourceFileEdit);
  }

  getDocumentSourceFile(documentNumber: string, documentVersion: string): Observable<any> {
    return this.httpClient.get(ApiConstants.CreateOrUpdateDocumentSourceFile_APi + "/" + `${documentNumber}` + "/"
      + `${documentVersion}`);
  }

  copyDocumentSourceFile(documentNumber: string, documentVersion: string): Observable<any> {
    return this.httpClient.post(ApiConstants.CopyDocumentSourceFile_Api + `${documentNumber}` + '/' + `${documentVersion}`, {
      params: {
        documentNumber: documentNumber,
        documentVersion: documentVersion
      }
    });
  }

  getPendingAndNextPeriodicChangeRequestItemsForDocument(documentNumber: string, documentVersion: number, documentPhase: number)
    : Observable<any> {
    return this.httpClient.get(ApiConstants.GetChangeRequestItemsForReviewAndApproval,
      {
        params: {
          documentNumber: documentNumber,
          version: documentVersion,
          documentPhase: documentPhase
        }
      });
  }

  updatePendingChangeRequestItemStatus(documentNumber: string): Observable<any> {
    return this.httpClient.put(
      ApiConstants.UpdateChangeRequestItemsToPendingStatus_Api + documentNumber, ""
    );
  }

  generateDocumentFormattedFilePreview(documentNumber: string, documentVersion: string): Observable<any> {
    return this.httpClient.post(ApiConstants.GenerateDocumentFormattedFilePreview_Api + "/" + `${documentNumber}` + "/"
      + `${documentVersion}`, null);
  }

  getDocumentDetailsForDocumentFormattedFile(documentNumber: string, documentVersion: string): Observable<any> {
    return this.httpClient.get(ApiConstants.DocumentDetailsForDocumentFormattedFile_API + "/" + `${documentNumber}` + "/"
      + `${documentVersion}`);
  }
  setPeriodicReviewDateForChangeRequestItem(documentNumber: string, documentVersion: string, periodicReviewData: any, skipPolling: boolean = false): Observable<any> {
    const options = skipPolling ? { headers: new HttpHeaders().set('X-Skip-Polling', 'true') } : {};
    return this.httpClient.put(
      ApiConstants.Set_PeriodicReviewDate_For_DocumentVersion + '/' + documentNumber + '/' + documentVersion, periodicReviewData, options
    );
  }

  updateDocumentFavorite(documentNumber: string): Observable<any> {
    return this.httpClient.put(ApiConstants.DocumentFavorite_Api + '/' + `${documentNumber}`, null);
  }

  checkIsDocumentFavorite(documentNumber: string): Observable<any> {
    return this.httpClient.get(ApiConstants.DocumentFavorite_Api + '/' + 'checkForDocument' + '/' + `${documentNumber}`)
  }

  getAssignmentGroups(assignmentGroupsRequest: AssignmentGroupsRequest) {
    return this.httpClient.post(ApiConstants.AssignmentGroups_OnScopeChange_API, assignmentGroupsRequest)
  }

  addAuditForDocumentRead(documentReadAudit: DocumentReadAudit): Observable<any> {
    return this.httpClient.post(
      ApiConstants.Document_Read_Audit_Api,
      documentReadAudit
    );
  }

  //Get Document Proficiency Quiz 
  getDocumentProficiencyQuizDetails = (documentNumber: string, documentVersion: number): Observable<DocumentProficiencyQuiz> => {
    let observable: Observable<DocumentProficiencyQuiz>;
    if (Object.keys(this.documentProficiencyQuizDetail).length === 0) {
      observable = this.httpClient
        .get<DocumentProficiencyQuiz>(ApiConstants.Document_Proficiency_Quiz_Api + '/' + `${documentNumber}` + '/' + `${documentVersion}`)
        .pipe(
          tap(documentProficiencyQuizDetails => this.documentProficiencyQuizDetail = documentProficiencyQuizDetails)
        );
    } else {
      observable = of(this.documentProficiencyQuizDetail);
    }
    return observable;
  }

  // Save Document Proficiency Quiz
  saveDocumentProficiencyQuiz = (documentProficiencyQuiz: DocumentProficiencyQuiz): Observable<DocumentProficiencyQuiz> => {
    let observable: Observable<DocumentProficiencyQuiz>;
    if (documentProficiencyQuiz.documentProficiencyQuizId === null) {
      observable = this.httpClient
        .post<DocumentProficiencyQuiz>(ApiConstants.Document_Proficiency_Quiz_Api, documentProficiencyQuiz)
        .pipe(
          tap(documentProficiencyQuizDetails => this.documentProficiencyQuizDetail = documentProficiencyQuizDetails),
          catchError((err) => {
            this.messageService.add({
              severity: DocumentLibraryConstants.Severity_Err,
              summary: `${err.error}`,
              life: 20000
            });
            return throwError(() => err);
          })
        );

    } else {
      observable = this.httpClient
        .put<DocumentProficiencyQuiz>(ApiConstants.Document_Proficiency_Quiz_Api, documentProficiencyQuiz)
        .pipe(
          tap(documentProficiencyQuizDetails => this.documentProficiencyQuizDetail = documentProficiencyQuizDetails),
          catchError((err) => {
            this.messageService.add({
              severity: DocumentLibraryConstants.Severity_Err,
              summary: `${err.error}`,
              life: 20000
            });
            return throwError(() => err);
          })
        );

    }
    return observable;
  }

  getTrainingDataForDocumentVersion(documentNumber:string, documentVersion: string): Observable<any>{
    return this.httpClient.post(ApiConstants.Training_Document+ `${documentNumber}` + '/' + `${documentVersion}`, {
      params: {
        documentNumber: documentNumber,
        documentVersion: documentVersion
      }
    });
  }

  updateTrainingPlanForDocumentTrainingItem(documentNumber:string, documentVersion:string):Observable<any>{
    return this.httpClient.post(ApiConstants.UpdateTraining_Document+ `${documentNumber}` + '/' + `${documentVersion}`, {
      params: {
        documentNumber: documentNumber,
        documentVersion: documentVersion
      }
    });
  }

  CalculateQuizResultsForQuizAttempt(quizAttemptData:any):Observable<any>{
    return this.httpClient.post(ApiConstants.TrainingPlan_Api+ "/calculateQuizResultsForQuizAttempt", quizAttemptData
    );
  }

  updateEffectiveAndPeriodicReviewDate(documentEffectiveAndPeriodicReviewDto: DocumentEffectiveAndPeriodicReviewDateDto):Observable<any>{
    return this.httpClient.put(ApiConstants.Document_UpdateEffectiveAndPeriodicReviewDate_Api,documentEffectiveAndPeriodicReviewDto );
  }

  getTrainingItemGroupForDocument(documentNumber:string) : Observable<TrainingItemGroupWithScheduled[]>{
    return this.httpClient.get<TrainingItemGroupWithScheduled[]>
    (ApiConstants.Get_TrainingItemGroups_For_Document.replace('{documentNumber}',documentNumber));
  }

  getCurriculumsForActiveDocumentTrainingItem(documentNumber:string) : Observable<NameCode[]>{
    return this.httpClient.get<NameCode[]>(ApiConstants.Get_Curriculums_For_Document.replace('{documentNumber}',documentNumber));
  }
  getCurriculumsByScheduledTrainingItem(documentNumber:string) : Observable<NameCode[]>{
    return this.httpClient.get<NameCode[]>(ApiConstants.Get_ScheduledCurriculums_For_Document.replace('{documentNumber}',documentNumber));
  }

  createDocumentCommand(documentCommentDto : DocumentCommentDTO) : Observable<any>{
    return this.httpClient.post(ApiConstants.CreateDocumentComment_Api, documentCommentDto);
  }

  getBulkUserReplacementLookup() : Observable<any> {
    let observable: Observable<any>;
    if (Object.keys(this.documentBulkUserReplacementLookup).length === 0) {
      observable = this.httpClient
      .get(ApiConstants.Get_BulkUserReplacementLookup_Api)
      .pipe(tap((documentData) => (this.documentBulkUserReplacementLookup = documentData)));
    } else {
      observable = of(this.documentBulkUserReplacementLookup);
    }
    return observable;
  }

  updateBulkUserForDocument(userReplacementDetails: UserReplacementDetails): Observable<any> {
    return this.httpClient.post(ApiConstants.Update_BulkUserReplacementForDocument_Api, userReplacementDetails);
  }

  updateRelatedDocuments(documentNumber: string, documentVersion: number, relatedDocuments: string[]): Observable<any> {
    return this.httpClient.put(
      ApiConstants.Document_RelatedDocuments_Api + '/' + `${documentNumber}` + '/' + `${documentVersion}`,
      relatedDocuments
    );

  }

  queryDocumentLibraryData(
    paginationState: PaginationState,
    filters?: AppliedFilters | null,
    sort?: SortCriteria | null,
    primarySearchText?: string | null,
    secondarySearchText?: string | null,
    users?: string[] | null,
    panelEQMSScope?: string[] | null
  ): Observable<DocumentQueryResult> {

    const filtersForMapping = filters ? { ...filters, searchText: undefined } : null;

    
    const base = this.searchMapper.mapToBaseSearchRequest(filtersForMapping, sort, paginationState);
    base.Filter = base.Filter || { SearchText: null, FilterItems: [] };
    base.Filter.SearchText = primarySearchText || null;

    const payload = {
      ...base,
      SecondarySearchText: secondarySearchText || null,
      Users: users || null,
      PanelEQMSScope: panelEQMSScope || null
    };

    return this.httpClient.post<DocumentQueryResult>(ApiConstants.Document_Search_Api, payload);
  }
}
